import React from 'react';

import {
  AccountAvgRatingInfo,
  NetworkSlug,
  ProductSlug,
  Rating,
  RedirectLinkProps,
  VersionSlug,
  ratingToLetter,
} from 'domain/types';
import { NextLink, PositionName } from 'components';
import { colors, styled } from 'core/styles';
import { ratingColors } from 'domain/utils';

import { getRatingLabel } from './utils';
import { Join } from './Join';

type LinkMakerParams = {
  marketId: string;
  network: NetworkSlug;
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
};

type TranscriptionProps = AccountAvgRatingInfo & {
  disableLinks: boolean;
  makeMarketRatingsLinkProps: (params: LinkMakerParams) => RedirectLinkProps;
  makeProtocolRatingsLinkProps: (params: Omit<LinkMakerParams, 'marketId'>) => RedirectLinkProps;
};

export function AccountAvgRatingTranscription({
  accountRating,
  mostRiskyPosition,
  disableLinks,
  makeMarketRatingsLinkProps,
  makeProtocolRatingsLinkProps,
}: TranscriptionProps) {
  const portfolioRating = [
    'Portfolio rating is ',
    <RatingBox key="portfolio-rating" rating={accountRating}>
      {ratingToLetter(accountRating)}&nbsp;·&nbsp;{getRatingLabel(accountRating)}
    </RatingBox>,
  ];

  if (!mostRiskyPosition) {
    return portfolioRating;
  }

  const {
    positionRating,
    isMultiMarketProtocol,
    marketVid,
    marketName,
    productSlug,
    versionSlug,
    productName,
    network,
    share,
  } = mostRiskyPosition;
  const estimatedAccountRating = Math.round(mostRiskyPosition.estimatedAccountRating) as Rating;

  const positionName = (
    <PositionNameBox>
      <PositionName
        productSlug={productSlug}
        productName={productName}
        network={network}
        marketName={marketName}
        maxWidth={150}
      />
    </PositionNameBox>
  );

  const ratingPosition = [
    'The riskiest position is in ',
    disableLinks ? (
      positionName
    ) : (
      <MarketRatingsLink
        href={
          isMultiMarketProtocol
            ? makeMarketRatingsLinkProps({
                marketId: marketVid,
                network,
                productSlug,
                versionSlug,
              }).href
            : makeProtocolRatingsLinkProps({ productSlug, versionSlug, network }).href
        }
        key="market-link"
      >
        {positionName}
      </MarketRatingsLink>
    ),
    ', holding a',
    <>&nbsp;</>,
    <RatingBox key="position-rating" rating={positionRating}>
      {ratingToLetter(positionRating)}
    </RatingBox>,
    ' credit rating',
  ];

  // hide if the worst position has a share of more than 99.5%
  const portfolioRatingForecast =
    share < 99.5 && estimatedAccountRating !== accountRating
      ? [
          "Excluding this position, the portfolio's overall rating would improve to",
          <>&nbsp;</>,
          <RatingBox key="estimated-account-rating" rating={estimatedAccountRating}>
            {ratingToLetter(estimatedAccountRating)}
          </RatingBox>,
        ]
      : null;

  return <Join separator=". " items={[portfolioRating, ratingPosition, portfolioRatingForecast]} />;
}

const RatingBox = styled('span', { shouldForwardProp: prop => prop !== 'rating' })<{
  rating: Rating;
}>(({ rating }) => ({
  color: ratingColors[rating],
}));

const MarketRatingsLink = styled(NextLink)({
  transition: 'opacity 200ms',
  '&:hover': {
    opacity: 0.7,
  },
});

const PositionNameBox = styled('div')({
  display: 'inline-block',
  background: colors.blackOpacity[8],
  borderRadius: 100,
  padding: '0px 8px 0px 4px',
});
